import { Fragment, useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReactComponent as DownloadIcon } from "../../../assets/images/download.svg";
import { ReactComponent as CloseIcon } from "../../../assets/images/closeContent.svg";
import { SourceSheet } from "../../core/SourceSheet/SourceSheet";
import { Streamer } from "../../core/Streamer/Streamer";
import { HartmanLinkComp } from "../../core/HartmanLink/HartmanLink";
import { hasVideo, openLink, sendFileAsAttachment } from "../../content/Globals/globals";
import contentService from "../../../Axios/services/content.service";
import MediaContainer from "./MediaContainer";
import FullContentSkeleton from "./FullContentSkeleton";
import "../../../assets/fonts/customFonts.css";
import "./FullContentPage.scss";

const FullContentPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [content, setContent] = useState<any>(undefined);
  const [attachmentSent, setAttachmentSent] = useState<any>(false);
  const [mounted, setMounted] = useState(false);
  const { me } = useSelector((state: any) => state.auth);

  const getContent = useCallback(async () => {
    try {
      const content = await contentService.get(id);
      return content;
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    getContent().then((res) => {
      setContent(res);
      setMounted(true);
    });
  }, [mounted, me, getContent]);

  const renderMedia = (content: any) => {
    let media;
    if (hasVideo(content)) {
      media = <Streamer file={content.media[0]?.file} link={content?.media[0]?.link}></Streamer>;
    }
    return media;
  };

  const renderThumbnail = (content: any) => {
    let photo;
    if (content?.thumbnail) {
      photo = (
        <div className="row justify-content-center">
          <img className="post-photo" src={content?.thumbnail} alt="Content thumbnail" />
        </div>
      );
    }
    return photo;
  };

  const renderSefaria = (content: any) => {
    return content.type === "sefaria_link" ? <SourceSheet source={content.media[0]} contentMeta={content} /> : null;
  };

  const renderHartmanLink = (content: any) => {
    return content.type === "hartman_link" && content.media[0] && content.media[0].content ? <HartmanLinkComp json={content.media[0].content} /> : null;
  };

  const renderFileDownload = (content) => {
    if (content.media[0] && content.type === "file") {
      const { azureLink, mimetype } = content.media[0];
      const onDownload = (e) => openLink(azureLink, e);
      const onShare = (e) => sendFileAsAttachment(azureLink, e, me, setAttachmentSent);

      if (mimetype === "application/pdf") {
        return <MediaContainer link={azureLink} onDownload={onDownload} onShare={onShare} isPdf />;
      } else if (mimetype.includes("image")) {
        return <MediaContainer link={azureLink} onDownload={onDownload} onShare={onShare} isImage />;
      } else if (
        mimetype === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        mimetype === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        mimetype === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        mimetype === "application/vnd.ms-powerpoint"
      ) {
        return <MediaContainer link={azureLink} onDownload={onDownload} onShare={onShare} />;
      }
    }

    return (
      <div className="row justify-content-center align-items-center my-3">
        <button className="downloadButton downloadFixed" onClick={(e) => openLink(content.media[0].azureLink, e)}>
          <DownloadIcon className="downloadIcon" />
        </button>
      </div>
    );
  };

  const renderTags = (item: any) => {
    return item?.tags && item.tags.length > 0 ? (
      <div className="tags-wrap">
        Tags: &nbsp;
        {item?.tags?.map((el: any, index: number) => (
          <div key={index} className="tag">
            {el}
          </div>
        ))}
      </div>
    ) : null;
  };

  const navigateBackToEntity = () => {
    let currentPath = localStorage.getItem("currentPath");
    if (!currentPath || currentPath.length < 1) {
      currentPath = "/my-feed";
    }
    localStorage.removeItem("currentPath");
    currentPath && navigate(currentPath);
  };


  return content ? (
    <div className="full-post">
      <div className={"goBackFullPage pointer"} onClick={() => navigateBackToEntity()}>
        <CloseIcon className={"goBackFullPage pointer"} />
      </div>

      <div className="post-summary">
        <div className="post-title-wrap d-flex flex-wrap justify-content-center align-items-center">
          {content?.title && (
            <div className="p-0 col-12 col-md-12 d-flex align-items-center justify-content-center mb-3">
              <div className="post-title">{decodeURIComponent(content.title)}</div>
            </div>
          )}
          {content?.description && (
            <div className="p-0 col-12 col-md-12 d-flex align-items-center justify-content-center">
              <div className="post-description">{content?.description}</div>
            </div>
          )}
        </div>
      </div>
      {renderSefaria(content)}
      {renderHartmanLink(content)}
      {renderMedia(content)}
      {renderFileDownload(content)}
      {renderThumbnail(content)}

      {renderTags(content)}
      {content?.related?.length > 0 ? (
        <Fragment>
          <div className="more-info">
            <div className="mb-3 list-group">More from the collection</div>
            {content?.related?.map((el: any, index: number) => (
              <div
                key={index}
                className="list-group-item list-group-item-action content-item-url"
                onClick={() => {
                  navigate("/content/" + el._id);
                }}
              >
                <span className="mb-1" dir={"ltr"}>
                  {el.title}
                </span>
              </div>
            ))}
          </div>
        </Fragment>
      ) : null}
      <div className="space"></div>
      {attachmentSent ? (
        <div className="attachmentSentMsg">
          {content.title} was sent to your email ({me.email})
        </div>
      ) : null}
    </div>
  ) : (
    <FullContentSkeleton />
  );
};

export default FullContentPage;
